import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatElapsed, useRunningTasks } from '@/application/timer/timerStore';
import { usePauseWork, useRunningTasks as useRunningTasksFromServer } from '@/application/usecases/useKits';
import { strings } from '@/shared/strings';
import { PauseIcon } from '@/ui/icons';
import { PauseConfirm } from './PauseConfirm';
import { usePresence } from '@/ui/motion/usePresence';
import { useListMotion } from '@/ui/motion/useListMotion';
import styles from './RunningStrip.module.css';

/**
 * Every task this person has running, with its clock and a Pause on each.
 * Section 2.4 of the request of 20 September 2026.
 *
 * Under the top bar and on every screen, because a technician can now have three
 * trays counting and be looking at none of them. Without this, the only way to
 * stop a case is to navigate to it, and a case somebody forgot is a case that
 * accrues wall clock all night: section 1 removed the idle pause that used to
 * catch that.
 *
 * It also seeds the clocks. `useRunningTasks` from the use cases asks the server
 * what is running and writes it into the store, which is what makes a reopened
 * browser show the right time rather than zero (section 1.5). Mounting that here,
 * in the one component that is always on screen for a bench role, means no screen
 * has to remember to do it.
 *
 * Absent rather than empty when nothing is running: an empty strip on every
 * screen would be a permanent band of nothing.
 */
export function RunningStrip() {
  const navigate = useNavigate();
  const pauseWork = usePauseWork();
  const fromServer = useRunningTasksFromServer();
  const live = useRunningTasks();
  const [pausing, setPausing] = useState<string | null>(null);
  // When the last task stops, the strip slides away rather than vanishing.
  const last = useRef(live);
  if (live.length > 0) last.current = live;
  const { shown, leaving } = usePresence(live.length > 0);
  const running = live.length > 0 ? live : last.current;
  const list = useListMotion<HTMLUListElement>(running.map((task) => task.kitId));

  if (!shown || running.length === 0) return null;

  /** The job order number, from the server's list; the kit id is the fallback. */
  const labelOf = (kitId: string): string =>
    (fromServer.data ?? []).find((task) => task.kitId === kitId)?.jobOrderNo ?? kitId;

  return (
    <div
      className={`${styles.strip} ${leaving ? styles.leaving : ''}`}
      role="status"
      aria-label={strings.running.label}
    >
      <span className={styles.heading}>
        {running.length === 1 ? strings.running.one : `${running.length} ${strings.running.many}`}
      </span>

      <ul ref={list} className={styles.list}>
        {running.map((task) => (
          <li key={task.kitId} data-motion-key={task.kitId} className={styles.item}>
            <button
              type="button"
              className={styles.open}
              onClick={() => void navigate(`/queue/${task.kitId}`)}
            >
              <span className={styles.live} />
              <span className={styles.clock}>{formatElapsed(task.elapsedSeconds)}</span>
              <span className={styles.jo}>{labelOf(task.kitId)}</span>
            </button>
            <button
              type="button"
              className={styles.pause}
              aria-label={`${strings.running.pause} ${labelOf(task.kitId)}`}
              onClick={() => setPausing(task.kitId)}
            >
              <PauseIcon />
            </button>
          </li>
        ))}
      </ul>
      {pausing !== null && (
        <PauseConfirm
          jobOrderNo={labelOf(pausing)}
          elapsedSeconds={running.find((task) => task.kitId === pausing)?.elapsedSeconds ?? 0}
          busy={pauseWork.isPending}
          onCancel={() => setPausing(null)}
          onConfirm={() =>
            pauseWork.mutate({ kitId: pausing }, { onSettled: () => setPausing(null) })
          }
        />
      )}
    </div>
  );
}
